import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Box, Button, CircularProgress, Divider, IconButton,
  InputBase, Stack, Tooltip, Typography, Avatar,
  Dialog, DialogTitle, DialogContent, DialogActions, Switch, FormControlLabel, TextField,
} from "@mui/material";
import {
  MdSend, MdSettings, MdAdd, MdClose,
  MdArrowBack, MdEdit, MdSignalCellular4Bar, MdSearch, MdDelete, MdNotificationsActive, MdLink,
} from "react-icons/md";
import { db, auth } from "../../firebase/firebase-config";

// ── Palette ───────────────────────────────────────────────────────────────────

const TG = {
  sidebarBg:    "#17212b",
  sidebarHover: "rgba(255,255,255,0.045)",
  chatBg:       "#0e1621",
  myBubble:     "#2b5278",
  theirBubble:  "#1e2c3a",
  headerBg:     "#17212b",
  border:       "rgba(255,255,255,0.08)",
  borderLight:  "rgba(255,255,255,0.05)",
  text:         "#e8edf2",
  textMuted:    "rgba(232,237,242,0.55)",
  textDim:      "rgba(232,237,242,0.30)",
  accent:       "#5288c1",
  accentHover:  "#3d72a8",
  accentFaint:  "rgba(82,136,193,0.12)",
  signalBuy:    "#4ade80",
  signalSell:   "#f87171",
  inputBg:      "#182533",
  selectedBg:   "rgba(82,136,193,0.14)",
};

const API_BASE = "https://us-central1-vanguard-e2803.cloudfunctions.net/telegramUserApi";

// Module-level cache
let _channelsCache = null;
const _msgsCache   = {};

// ── Helpers ────────────────────────────────────────────────────────────────────

async function getToken() {
  const user = auth.currentUser;
  if (!user) throw new Error("Not logged in");
  return user.getIdToken();
}

async function api(method, path, body) {
  const token = await getToken();
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Request failed");
  return data;
}

function fmtTime(ts) {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : new Date(ts.seconds * 1000);
  return d.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" });
}

function fmtDate(ts) {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : new Date(ts.seconds * 1000);
  const now = new Date();
  const diff = new Date(now.getFullYear(), now.getMonth(), now.getDate()) -
               new Date(d.getFullYear(), d.getMonth(), d.getDate());
  if (diff === 0)          return "Hoy";
  if (diff === 86400000)   return "Ayer";
  return d.toLocaleDateString("es-MX", { day: "numeric", month: "long" });
}

function detectSignal(text) {
  if (!text?.trim()) return null;
  // @ is optional; entry can be a range like "4625-4631" (first number captured)
  const m = text.split("\n")[0].match(
    /^([A-Z][A-Z0-9/]{2,11})\s*(?:\([^)]{1,40}\))?\s+(BUY\s*NOW|SELL\s*NOW|BUY|SELL)\s*(?:NOW\s*)?(?:@\s*)?([\d.,]+)/i
  );
  if (!m) return null;
  return {
    currency: m[1].toUpperCase(),
    type:     m[2].toUpperCase().startsWith("SELL") ? "SELL" : "BUY",
    price:    m[3],
  };
}

const CHANNEL_COLORS = ["#5288c1","#27ae60","#8e44ad","#d35400","#c0392b","#16a085","#2980b9","#7f8c8d"];
function channelColor(id) { return CHANNEL_COLORS[Math.abs((id || "").split("").reduce((s, c) => s + c.charCodeAt(0), 0)) % CHANNEL_COLORS.length]; }
function channelInitial(ch) { return (ch.name || "C").trim()[0].toUpperCase(); }

const ALEX_RE = /\balex\b|kingalex|@kingalex/i;

// Show messages that have text or media, and are not spam-filtered
function isDisplayable(msg) {
  if (msg.isFiltered) return false;
  if (ALEX_RE.test(msg.text || '')) return false;
  return !!(msg.text?.trim()) || !!(msg.mediaUrl);
}

// ── Sub-components ────────────────────────────────────────────────────────────

function DateSep({ label }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", my: 2.5, gap: 1.25, px: 1 }}>
      <Box sx={{ flex: 1, height: "1px", background: "linear-gradient(to right, transparent, rgba(255,255,255,0.07))" }} />
      <Box sx={{
        px: 1.5, py: 0.4,
        background: "rgba(14,22,33,0.85)",
        border: `1px solid rgba(255,255,255,0.07)`,
        borderRadius: "20px",
      }}>
        <Typography sx={{ color: TG.textDim, fontSize: 10.5, letterSpacing: 0.7, fontWeight: 500, whiteSpace: "nowrap" }}>
          {label}
        </Typography>
      </Box>
      <Box sx={{ flex: 1, height: "1px", background: "linear-gradient(to left, transparent, rgba(255,255,255,0.07))" }} />
    </Box>
  );
}

function SignalBadge({ sig }) {
  const isBuy = sig.type === "BUY";
  const color  = isBuy ? TG.signalBuy : TG.signalSell;
  return (
    <Box sx={{
      display: "inline-flex", alignItems: "center", gap: 0.75,
      mt: 0.875, px: 1.125, py: 0.5,
      background: isBuy ? "rgba(74,222,128,0.08)" : "rgba(248,113,113,0.08)",
      border: `1px solid ${isBuy ? "rgba(74,222,128,0.28)" : "rgba(248,113,113,0.28)"}`,
      borderRadius: "6px", maxWidth: "100%",
    }}>
      <MdSignalCellular4Bar size={11} color={color} style={{ flexShrink: 0 }} />
      <Typography noWrap sx={{ color, fontSize: 10.5, fontWeight: 700, letterSpacing: 0.8, lineHeight: 1.3 }}>
        SIGNAL · {sig.type} · {sig.currency}{sig.price ? ` @ ${sig.price}` : ""}
      </Typography>
    </Box>
  );
}

function MessageBubble({ msg, channel, onDelete }) {
  const [hovering,  setHovering]  = useState(false);
  const [deleting,  setDeleting]  = useState(false);
  const [notifying, setNotifying] = useState(false);
  const [notified,  setNotified]  = useState(false);
  const isAdmin = msg.sentByAdmin;
  const signal  = !isAdmin && detectSignal(msg.text);

  // Bell is shown only for signal messages (push notifications are signals-only).
  const canNotify = !isAdmin && !!msg.isSignal;

  async function handleDelete() {
    if (!window.confirm("¿Eliminar este mensaje del historial?")) return;
    setDeleting(true);
    try {
      await db.collection("telegram_messages").doc(msg.id).delete();
      onDelete(msg.id);
    } catch (e) {
      alert("Error al eliminar: " + e.message);
      setDeleting(false);
    }
  }

  async function handleNotify() {
    if (notified) return;
    setNotifying(true);
    try {
      await api("POST", "/notify", { docId: msg.id });
      setNotified(true);
      setTimeout(() => setNotified(false), 4000);
    } catch (e) {
      alert("Error al enviar notificación: " + e.message);
    } finally {
      setNotifying(false);
    }
  }

  return (
    <Box
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      sx={{
        display: "flex",
        justifyContent: isAdmin ? "flex-end" : "flex-start",
        mb: 0.75, px: 1.5,
        position: "relative",
        alignItems: "flex-end",
        gap: 0.75,
      }}
    >
      {/* Action buttons (appear on hover) */}
      {hovering && (
        <Box sx={{
          position: "absolute",
          top: "50%", transform: "translateY(-50%)",
          [isAdmin ? "left" : "right"]: 8,
          zIndex: 5,
          display: "flex", flexDirection: "column", gap: 0.5,
        }}>
          {canNotify && (
            <Tooltip title={notified ? "¡Enviada!" : "Enviar notificación"} placement={isAdmin ? "right" : "left"}>
              <IconButton size="small" onClick={handleNotify} disabled={notifying || notified}
                sx={{
                  width: 26, height: 26,
                  background: notified ? "rgba(74,222,128,0.18)" : "rgba(15,20,30,0.82)",
                  backdropFilter: "blur(6px)",
                  border: `1px solid ${notified ? "rgba(74,222,128,0.4)" : "rgba(255,255,255,0.07)"}`,
                  color: notified ? "#4ade80" : TG.textDim,
                  "&:hover": { background: "rgba(82,136,193,0.22)", color: TG.accent, borderColor: `${TG.accent}55` },
                }}>
                {notifying ? <CircularProgress size={11} /> : <MdNotificationsActive size={13} />}
              </IconButton>
            </Tooltip>
          )}
          <IconButton size="small" onClick={handleDelete} disabled={deleting}
            sx={{
              width: 26, height: 26,
              background: "rgba(15,20,30,0.82)",
              backdropFilter: "blur(6px)",
              border: "1px solid rgba(255,255,255,0.07)",
              color: TG.textDim,
              "&:hover": { background: "rgba(248,113,113,0.18)", color: "#f87171", borderColor: "rgba(248,113,113,0.3)" },
            }}>
            {deleting ? <CircularProgress size={11} /> : <MdDelete size={13} />}
          </IconButton>
        </Box>
      )}

      <Box sx={{ maxWidth: "66%", minWidth: 0 }}>
        {/* Bubble */}
        <Box sx={{
          px: 2, py: 1.375,
          borderRadius: isAdmin ? "18px 18px 5px 18px" : "18px 18px 18px 5px",
          background: isAdmin
            ? `linear-gradient(135deg, ${TG.myBubble} 0%, #234a6b 100%)`
            : TG.theirBubble,
          boxShadow: isAdmin
            ? "0 2px 8px rgba(82,136,193,0.18)"
            : "0 1px 4px rgba(0,0,0,0.25)",
          border: `1px solid ${isAdmin ? "rgba(82,136,193,0.22)" : "rgba(255,255,255,0.055)"}`,
          overflow: "hidden",
        }}>
          {isAdmin && (
            <Typography sx={{ color: "rgba(122,184,235,0.9)", fontSize: 10.5, fontWeight: 700, mb: 0.625, letterSpacing: 0.5, textTransform: "uppercase" }}>
              Admin
            </Typography>
          )}
          {msg.mediaUrl && (
            <Box
              component="img"
              src={msg.mediaUrl}
              alt="foto"
              onClick={() => window.open(msg.mediaUrl, "_blank")}
              sx={{
                display: "block",
                maxWidth: "100%",
                maxHeight: 300,
                objectFit: "contain",
                borderRadius: "8px",
                mb: msg.text ? 1 : 0,
                cursor: "pointer",
                "&:hover": { opacity: 0.88 },
              }}
            />
          )}
          {msg.text && (
            <Typography sx={{
              color: TG.text,
              fontSize: 13.5,
              whiteSpace: "pre-wrap",
              lineHeight: 1.65,
              wordBreak: "break-word",
              overflowWrap: "anywhere",
            }}>
              {msg.text}
            </Typography>
          )}
          {signal && <SignalBadge sig={signal} />}
          <Typography sx={{
            color: TG.textDim,
            fontSize: 9.5,
            textAlign: "right",
            mt: 0.875,
            letterSpacing: 0.3,
          }}>
            {fmtTime(msg.date)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

function ChannelItem({ ch, selected, onClick, lastMsg }) {
  const color = channelColor(ch.id);
  const preview = lastMsg
    ? (() => {
        const prefix = lastMsg.sentByAdmin ? "Tú: " : "";
        if (lastMsg.text?.trim()) return prefix + lastMsg.text.trim();
        if (lastMsg.mediaUrl)    return prefix + "📷 Foto";
        return null;
      })()
    : null;

  return (
    <Box onClick={() => onClick(ch)} sx={{
      display: "flex", alignItems: "center", gap: 1.5,
      px: 1.75, py: 1.25, cursor: "pointer",
      background: selected ? TG.selectedBg : "transparent",
      borderLeft: `3px solid ${selected ? TG.accent : "transparent"}`,
      transition: "background 0.15s, border-color 0.15s",
      "&:hover": { background: selected ? TG.selectedBg : TG.sidebarHover },
    }}>
      <Avatar sx={{
        width: 44, height: 44,
        background: `linear-gradient(135deg, ${color}, ${color}bb)`,
        fontSize: 16, fontWeight: 800,
        flexShrink: 0, letterSpacing: 0,
        boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
      }}>
        {channelInitial(ch)}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={0.5}>
          <Typography sx={{
            color: selected ? "#fff" : TG.text,
            fontSize: 13, fontWeight: selected ? 700 : 500,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1,
          }}>
            {ch.name}
          </Typography>
          {lastMsg && (
            <Typography sx={{ color: TG.textDim, fontSize: 10, flexShrink: 0 }}>
              {fmtTime(lastMsg.date)}
            </Typography>
          )}
        </Stack>
        <Typography sx={{
          color: selected ? TG.textMuted : TG.textDim,
          fontSize: 11.5, mt: 0.25,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {preview ?? <em style={{ fontStyle: "italic", opacity: 0.6 }}>Sin mensajes aún</em>}
        </Typography>
      </Box>
    </Box>
  );
}

function ChannelDialog({ open, onClose, onSaved, editChannel }) {
  const [name,         setName]         = useState("");
  const [id,           setId]           = useState("");
  const [uname,        setUname]        = useState("");
  const [topicId,      setTopicId]      = useState("");
  const [active,       setActive]       = useState(true);
  const [saving,       setSaving]       = useState(false);
  const [error,        setError]        = useState("");
  const [detecting,    setDetecting]    = useState(false);
  const [topics,       setTopics]       = useState(null); // null = not fetched yet

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      background: TG.inputBg,
      "& fieldset": { borderColor: TG.border },
      "&:hover fieldset": { borderColor: `${TG.accent}55` },
      "&.Mui-focused fieldset": { borderColor: TG.accent },
    },
    "& .MuiInputBase-input":  { color: TG.text,     fontSize: 13.5 },
    "& .MuiInputLabel-root":  { color: TG.textMuted, fontSize: 13  },
    "& .MuiInputLabel-root.Mui-focused": { color: TG.accent },
  };

  const switchSx = {
    "& .Mui-checked": { color: TG.accent },
    "& .Mui-checked + .MuiSwitch-track": { backgroundColor: TG.accent },
  };

  useEffect(() => {
    if (open) {
      setName(editChannel?.name || "");
      setId(editChannel?.channelId || "");
      setUname(editChannel?.channelUsername || "");
      setTopicId(editChannel?.topicId != null ? String(editChannel.topicId) : "");
      setActive(editChannel?.isActive !== false);
      setError("");
      setTopics(null);
    }
  }, [open, editChannel]);

  async function detectTopics() {
    setDetecting(true); setTopics(null); setError("");
    try {
      let data;
      if (editChannel?.id) {
        // Existing channel — look up by Firestore doc ID (backend resolves channelId from doc)
        data = await api("GET", `/channels/${editChannel.id}/topics`);
      } else {
        // New channel — send the channelId typed in the form directly
        data = await api("POST", "/detect-topics", { channelId: id.trim() });
      }
      setTopics(data.topics || []);
      if ((data.topics || []).length === 0) setError("No se encontraron tópicos. Asegúrate de que el canal es un grupo con tópicos de foro activos.");
    } catch (e) { setError("Error detectando tópicos: " + e.message); }
    finally { setDetecting(false); }
  }

  async function handleSave() {
    if (!id.trim() && !uname.trim()) { setError("Ingresa Channel ID o Username"); return; }
    if (!name.trim()) { setError("Ingresa un nombre para el canal"); return; }
    setSaving(true); setError("");
    try {
      const tidPayload = topicId.trim() ? Number(topicId.trim()) : null;
      if (editChannel?.id) {
        await api("PUT", `/channels/${editChannel.id}`, {
          name, channelId: id, channelUsername: uname, isActive: active, topicId: tidPayload,
        });
      } else {
        await api("POST", "/channels", {
          name, channelId: id, channelUsername: uname, topicId: tidPayload,
        });
      }
      onSaved(); onClose();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { background: TG.sidebarBg, border: `1px solid ${TG.border}`, borderRadius: "14px" } }}>
      <DialogTitle sx={{ color: TG.text, fontSize: 15, fontWeight: 700, pb: 1 }}>
        {editChannel ? "Editar canal" : "Agregar canal"}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField label="Nombre del canal" value={name} onChange={e => setName(e.target.value)} fullWidth size="small" sx={fieldSx} />
          <TextField label="Channel ID  (ej. -1001234567890)" value={id} onChange={e => setId(e.target.value)} fullWidth size="small" sx={fieldSx} />
          <TextField label="Username  (ej. @micanal)" value={uname} onChange={e => setUname(e.target.value)} fullWidth size="small" sx={fieldSx} />

          {/* ── Forum / Community topic ── */}
          <Box sx={{ borderTop: `1px solid ${TG.border}`, pt: 1.5 }}>
            <Typography sx={{ color: TG.textDim, fontSize: 10.5, fontWeight: 700, letterSpacing: 1, mb: 1, textTransform: "uppercase" }}>
              Tópico de comunidad (opcional)
            </Typography>
            <Typography sx={{ color: TG.textDim, fontSize: 11.5, mb: 1.5, lineHeight: 1.5 }}>
              Si el canal es una comunidad con sub-grupos, ingresa el ID del tópico específico.
              El Chat ID del grupo es el mismo para todos; el Topic ID identifica cada sub-grupo.
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                label="Topic ID (sub-grupo)"
                value={topicId}
                onChange={e => setTopicId(e.target.value.replace(/[^0-9]/g, ""))}
                size="small"
                sx={{ ...fieldSx, flex: 1 }}
                placeholder="ej. 12345"
                inputProps={{ inputMode: "numeric" }}
              />
              {(editChannel?.id || id.trim()) && (
                <Button
                  onClick={detectTopics}
                  disabled={detecting}
                  size="small"
                  variant="outlined"
                  sx={{ borderColor: `${TG.accent}55`, color: TG.accent, textTransform: "none", fontSize: 12, whiteSpace: "nowrap",
                    "&:hover": { borderColor: TG.accent, background: `${TG.accent}11` },
                    "&.Mui-disabled": { borderColor: `${TG.border}`, color: TG.textDim } }}
                >
                  {detecting ? <CircularProgress size={12} sx={{ color: TG.accent }} /> : "Detectar"}
                </Button>
              )}
            </Box>

            {/* Discovered topics list */}
            {topics !== null && topics.length > 0 && (
              <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Typography sx={{ color: TG.textDim, fontSize: 11, mb: 0.5 }}>
                  Tópicos encontrados — haz clic para seleccionar:
                </Typography>
                {topics.map(t => {
                  const isSelected = String(t.topicId) === topicId;
                  return (
                    <Box
                      key={t.topicId}
                      onClick={() => setTopicId(String(t.topicId))}
                      sx={{
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        px: 1.5, py: 0.8, borderRadius: "8px", cursor: "pointer",
                        border: `1px solid ${isSelected ? TG.accent : TG.border}`,
                        background: isSelected ? `${TG.accent}18` : TG.inputBg,
                        "&:hover": { borderColor: `${TG.accent}88` },
                      }}
                    >
                      <Box>
                        <Typography sx={{ color: TG.text, fontSize: 12.5, fontWeight: isSelected ? 600 : 400 }}>
                          {t.title}
                        </Typography>
                        <Typography sx={{ color: TG.textDim, fontSize: 10.5, fontFamily: "monospace" }}>
                          ID: {t.topicId}
                        </Typography>
                      </Box>
                      {isSelected && (
                        <Typography sx={{ color: TG.accent, fontSize: 10.5, fontWeight: 600 }}>
                          ✓ Seleccionado
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>

          {editChannel && (
            <FormControlLabel
              label={<Typography sx={{ color: TG.textMuted, fontSize: 12.5 }}>Canal activo (monitorear)</Typography>}
              control={<Switch checked={active} onChange={e => setActive(e.target.checked)} size="small" sx={switchSx} />}
            />
          )}
          {error && <Typography sx={{ color: "#f87171", fontSize: 12 }}>{error}</Typography>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} disabled={saving}
          sx={{ color: TG.textMuted, textTransform: "none", fontSize: 13, borderRadius: "8px", "&:hover": { background: TG.sidebarHover } }}>
          Cancelar
        </Button>
        <Button onClick={handleSave} disabled={saving} variant="contained"
          sx={{ background: TG.accent, textTransform: "none", fontSize: 13, borderRadius: "8px", fontWeight: 600, "&:hover": { background: TG.accentHover }, "&.Mui-disabled": { background: `${TG.accent}55` } }}>
          {saving ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : editChannel ? "Guardar" : "Agregar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────

// ── Session status bar ─────────────────────────────────────────────────────────
// Sits at the bottom of the sidebar. Shows connection state, account, last sync,
// session age — and acts as the primary re-auth trigger when the session is dead.

function SessionStatusBar({ onOpen, refreshKey }) {
  const [auth,      setAuth]      = useState(null);
  const [lock,      setLock]      = useState(undefined); // undefined = not yet loaded
  const [rotatedAt, setRotatedAt] = useState(null);
  const [lastSync,  setLastSync]  = useState(null);

  // Re-probe whenever the re-auth dialog closes after a successful login
  useEffect(() => {
    setAuth(null);
    api("GET", "/auth/status")
      .then(setAuth)
      .catch(() => setAuth({ authorized: false, reason: "error" }));
  }, [refreshKey]);

  // Live: is the poller currently holding the session lock?
  useEffect(() => (
    db.doc("telegram_config/session_lock")
      .onSnapshot(s => setLock(s.exists ? s.data() : null), () => setLock(null))
  ), []);

  // Live: when was the session last rotated (written by finishLogin)?
  useEffect(() => (
    db.doc("telegram_config/session_status")
      .onSnapshot(s => setRotatedAt(s.exists ? s.data()?.rotatedAt ?? null : null), () => {})
  ), []);

  // Live: most recently polled channel → last sync timestamp
  useEffect(() => (
    db.collection("telegram_channels")
      .orderBy("lastPolledAt", "desc").limit(1)
      .onSnapshot(snap => setLastSync(snap.docs[0]?.data()?.lastPolledAt ?? null), () => {})
  ), []);

  function rel(ts) {
    if (!ts) return null;
    const d   = ts.toDate ? ts.toDate() : new Date((ts.seconds ?? ts) * 1000);
    const sec = Math.round((Date.now() - d.getTime()) / 1000);
    if (sec < 5)     return "ahora mismo";
    if (sec < 90)    return "hace un momento";
    if (sec < 3600)  return `hace ${Math.floor(sec / 60)} min`;
    if (sec < 86400) return `hace ${Math.floor(sec / 3600)} h`;
    return `hace ${Math.floor(sec / 86400)} días`;
  }

  if (auth === null || lock === undefined) return null; // loading — don't flash

  const lockExpiry = lock?.expiresAt?.toDate?.();
  const isPolling  = !!(lock?.holder && lockExpiry && lockExpiry > new Date());
  const isRevoked  = !auth.authorized;
  const accountStr = auth?.account?.username ? `@${auth.account.username}` : auth?.account?.name ?? null;

  const dotColor = isRevoked ? "#f87171" : isPolling ? TG.accent : TG.signalBuy;
  const bgColor  = isRevoked ? "rgba(248,113,113,0.08)" : isPolling ? "rgba(82,136,193,0.07)"  : "rgba(74,222,128,0.06)";
  const bdColor  = isRevoked ? "rgba(248,113,113,0.22)" : isPolling ? "rgba(82,136,193,0.18)"  : "rgba(74,222,128,0.14)";
  const bgHover  = isRevoked ? "rgba(248,113,113,0.14)" : isPolling ? "rgba(82,136,193,0.12)"  : "rgba(74,222,128,0.10)";

  const label = isRevoked  ? "Sesión revocada — reconectar"
              : isPolling  ? "Sincronizando…"
              : accountStr ? `Conectado · ${accountStr}`
              : "Conectado";

  const sublabel = isRevoked
    ? (rotatedAt ? `Última vinculación ${rel(rotatedAt)}` : "Toca para volver a vincular")
    : lastSync   ? `Última sincronización ${rel(lastSync)}`
    : rotatedAt  ? `Vinculado ${rel(rotatedAt)}`
    : null;

  return (
    <Box
      onClick={onOpen}
      sx={{
        mx: 1.5, my: 1, px: 1.5, py: 0.875,
        borderRadius: "10px",
        background: bgColor,
        border: `1px solid ${bdColor}`,
        cursor: "pointer",
        userSelect: "none",
        transition: "background 0.15s, border-color 0.15s",
        "&:hover": {
          background: bgHover,
          borderColor: isRevoked ? "rgba(248,113,113,0.38)" : undefined,
        },
      }}
    >
      <Stack direction="row" alignItems="center" gap={1.25}>
        <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: dotColor, flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{
            color: dotColor, fontSize: 11.5, fontWeight: isRevoked ? 700 : 600,
            lineHeight: 1.25, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {label}
          </Typography>
          {sublabel && (
            <Typography sx={{
              color: isRevoked ? "rgba(248,113,113,0.6)" : TG.textDim,
              fontSize: 10, mt: 0.2, lineHeight: 1.3,
            }}>
              {sublabel}
            </Typography>
          )}
        </Box>
        <MdLink size={13} style={{ flexShrink: 0, color: dotColor, opacity: isRevoked ? 0.9 : 0.4 }} />
      </Stack>
    </Box>
  );
}

// ── Telegram account re-authentication ─────────────────────────────────────────
// Telegram permanently revokes a session if it is used from two places at once.
// This dialog re-links the account from the panel instead of a CLI script.

function ReconnectDialog({ open, onClose }) {
  const [step,      setStep]     = useState("phone"); // phone → code → password → done
  const [phone,     setPhone]    = useState("");
  const [code,      setCode]     = useState("");
  const [password,  setPassword] = useState("");
  const [busy,      setBusy]     = useState(false);
  const [error,     setError]    = useState("");
  const [status,    setStatus]   = useState(null);
  const [rotatedAt, setRotatedAt] = useState(null);
  const [lastSync,  setLastSync]  = useState(null);

  function rel(ts) {
    if (!ts) return null;
    const d   = ts.toDate ? ts.toDate() : new Date((ts.seconds ?? ts) * 1000);
    const sec = Math.round((Date.now() - d.getTime()) / 1000);
    if (sec < 90)    return "hace un momento";
    if (sec < 3600)  return `hace ${Math.floor(sec / 60)} min`;
    if (sec < 86400) return `hace ${Math.floor(sec / 3600)} h`;
    return `hace ${Math.floor(sec / 86400)} días`;
  }

  useEffect(() => {
    if (!open) return;
    setStep("phone"); setCode(""); setPassword(""); setError(""); setStatus(null);
    api("GET", "/auth/status")
      .then(setStatus)
      .catch(e => setError(e.message));
    db.doc("telegram_config/session_status").get()
      .then(s => setRotatedAt(s.exists ? s.data()?.rotatedAt ?? null : null))
      .catch(() => {});
    db.collection("telegram_channels").orderBy("lastPolledAt", "desc").limit(1).get()
      .then(snap => setLastSync(snap.docs[0]?.data()?.lastPolledAt ?? null))
      .catch(() => {});
  }, [open]);

  const run = async (fn) => {
    setBusy(true); setError("");
    try { await fn(); }
    catch (e) { setError(e.message || "Error"); }
    finally { setBusy(false); }
  };

  const sendCode = () => run(async () => {
    await api("POST", "/auth/send-code", { phone: phone.trim() });
    setStep("code");
  });

  const signIn = () => run(async () => {
    const r = await api("POST", "/auth/sign-in", { code: code.trim() });
    setStep(r.needsPassword ? "password" : "done");
  });

  const submitPassword = () => run(async () => {
    await api("POST", "/auth/password", { password });
    setStep("done");
  });

  const field = {
    "& .MuiOutlinedInput-root": {
      color: TG.text, background: TG.inputBg, borderRadius: "8px",
      "& fieldset": { borderColor: TG.border },
      "&:hover fieldset": { borderColor: `${TG.accent}55` },
      "&.Mui-focused fieldset": { borderColor: TG.accent },
    },
    "& .MuiInputLabel-root": { color: TG.textMuted, fontSize: 13 },
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : () => onClose(false)} maxWidth="xs" fullWidth
      PaperProps={{ sx: { background: TG.sidebarBg, border: `1px solid ${TG.border}`, borderRadius: "12px" } }}>
      <DialogTitle sx={{ color: TG.text, fontSize: 15, fontWeight: 700 }}>
        Conexión de Telegram
      </DialogTitle>
      <DialogContent>
        {status && (
          <Box sx={{
            mb: 2, px: 1.5, py: 1.25, borderRadius: "10px",
            background: status.authorized ? "rgba(74,222,128,0.08)" : "rgba(248,113,113,0.08)",
            border: `1px solid ${status.authorized ? "rgba(74,222,128,0.22)" : "rgba(248,113,113,0.22)"}`,
          }}>
            <Stack direction="row" alignItems="center" gap={1} mb={status.authorized && (rotatedAt || lastSync) ? 0.75 : 0}>
              <Box sx={{ width: 7, height: 7, borderRadius: "50%", flexShrink: 0,
                background: status.authorized ? TG.signalBuy : "#f87171" }} />
              <Typography sx={{ color: status.authorized ? TG.signalBuy : TG.signalSell, fontSize: 12, fontWeight: 700, flex: 1 }}>
                {status.authorized
                  ? `Conectado${status.account?.username ? ` · @${status.account.username}` : ""}${status.busy ? " · poller activo" : ""}`
                  : "Sesión no válida"}
              </Typography>
            </Stack>
            {status.authorized && (rotatedAt || lastSync) && (
              <Stack spacing={0.3} sx={{ pl: "19px" }}>
                {rotatedAt && (
                  <Typography sx={{ color: TG.textDim, fontSize: 10.5, lineHeight: 1.4 }}>
                    Sesión vinculada {rel(rotatedAt)}
                  </Typography>
                )}
                {lastSync && (
                  <Typography sx={{ color: TG.textDim, fontSize: 10.5, lineHeight: 1.4 }}>
                    Última sincronización {rel(lastSync)}
                  </Typography>
                )}
              </Stack>
            )}
            {!status.authorized && (
              <Typography sx={{ color: "rgba(248,113,113,0.65)", fontSize: 10.5, pl: "19px", mt: 0.25, lineHeight: 1.4 }}>
                {rotatedAt ? `Última vez activa ${rel(rotatedAt)}` : "Sigue los pasos para volver a vincular"}
              </Typography>
            )}
          </Box>
        )}

        {step === "phone" && (
          <>
            <Typography sx={{ color: TG.textMuted, fontSize: 12, mb: 1.5 }}>
              Introduce el número de la cuenta de Telegram que lee los canales. Debe ser miembro de los grupos.
            </Typography>
            <TextField autoFocus fullWidth size="small" label="Número con código de país"
              placeholder="+52..." value={phone} onChange={e => setPhone(e.target.value)}
              onKeyDown={e => e.key === "Enter" && phone.trim() && sendCode()} sx={field} />
          </>
        )}

        {step === "code" && (
          <>
            <Typography sx={{ color: TG.textMuted, fontSize: 12, mb: 1.5 }}>
              Telegram envió un código a {phone}. Introdúcelo aquí.
            </Typography>
            <TextField autoFocus fullWidth size="small" label="Código de verificación"
              value={code} onChange={e => setCode(e.target.value)}
              onKeyDown={e => e.key === "Enter" && code.trim() && signIn()} sx={field} />
          </>
        )}

        {step === "password" && (
          <>
            <Typography sx={{ color: TG.textMuted, fontSize: 12, mb: 1.5 }}>
              Esta cuenta tiene verificación en dos pasos. Introduce la contraseña.
            </Typography>
            <TextField autoFocus fullWidth size="small" type="password" label="Contraseña"
              value={password} onChange={e => setPassword(e.target.value)}
              onKeyDown={e => e.key === "Enter" && password && submitPassword()} sx={field} />
          </>
        )}

        {step === "done" && (
          <Box sx={{ py: 1 }}>
            <Box sx={{
              display: "flex", alignItems: "center", gap: 1.25,
              px: 1.5, py: 1.25, borderRadius: "10px",
              background: "rgba(74,222,128,0.08)", border: "1px solid rgba(74,222,128,0.22)",
            }}>
              <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: TG.signalBuy, flexShrink: 0 }} />
              <Box>
                <Typography sx={{ color: TG.signalBuy, fontSize: 12.5, fontWeight: 700, lineHeight: 1.3 }}>
                  Telegram reconectado correctamente
                </Typography>
                <Typography sx={{ color: "rgba(74,222,128,0.6)", fontSize: 10.5, mt: 0.2 }}>
                  Los mensajes se sincronizarán en el próximo ciclo del poller (~9 min)
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

        {error && (
          <Typography sx={{ color: TG.signalSell, fontSize: 12, mt: 1.5 }}>{error}</Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={() => onClose(step === "done")}
          disabled={busy}
          sx={{ color: TG.textMuted, textTransform: "none", fontSize: 13 }}>
          {step === "done" ? "Cerrar" : "Cancelar"}
        </Button>
        {step === "phone" && (
          <Button onClick={sendCode} disabled={busy || !phone.trim()} variant="contained"
            sx={{ background: TG.accent, textTransform: "none", fontSize: 13, "&:hover": { background: TG.accentHover } }}>
            {busy ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : "Enviar código"}
          </Button>
        )}
        {step === "code" && (
          <Button onClick={signIn} disabled={busy || !code.trim()} variant="contained"
            sx={{ background: TG.accent, textTransform: "none", fontSize: 13, "&:hover": { background: TG.accentHover } }}>
            {busy ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : "Verificar"}
          </Button>
        )}
        {step === "password" && (
          <Button onClick={submitPassword} disabled={busy || !password} variant="contained"
            sx={{ background: TG.accent, textTransform: "none", fontSize: 13, "&:hover": { background: TG.accentHover } }}>
            {busy ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : "Confirmar"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default function TelegramView() {
  const [channels,    setChannels]    = useState(_channelsCache || []);
  const [selectedCh,  setSelectedCh]  = useState(_channelsCache?.[0] || null);
  const [messages,    setMessages]    = useState([]);
  const [lastMsgs,    setLastMsgs]    = useState({});
  const [loadingCh,   setLoadingCh]   = useState(!_channelsCache);
  const [chError,     setChError]     = useState("");
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [text,        setText]        = useState("");
  const [sending,     setSending]     = useState(false);
  const [sendError,   setSendError]   = useState("");
  const [chDialog,    setChDialog]    = useState(false);
  const [editCh,      setEditCh]      = useState(null);
  const [mobileView,  setMobileView]  = useState("list");
  const [reconnectOpen,   setReconnectOpen]   = useState(false);
  const [sessionRefresh, setSessionRefresh]  = useState(0);
  const [search,      setSearch]      = useState("");
  const bottomRef = useRef(null);
  const unsubRef  = useRef(null);

  const loadChannels = useCallback(async (force = false) => {
    if (_channelsCache && !force) return;
    setLoadingCh(true);
    setChError("");
    try {
      const chs = await api("GET", "/channels");
      _channelsCache = Array.isArray(chs) ? chs : [];
      setChannels(_channelsCache);
      setSelectedCh(prev => prev ?? (_channelsCache.length > 0 ? _channelsCache[0] : null));
    } catch (e) {
      console.error(e);
      setChError(e.message || "Error al cargar canales");
    }
    finally { setLoadingCh(false); }
  }, []);

  // Wait for Firebase Auth to restore the session before hitting the API.
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(user => {
      if (user) loadChannels();
      else setLoadingCh(false);
    });
    return () => unsub();
  }, [loadChannels]);

  // Last-message preview per channel
  useEffect(() => {
    if (channels.length === 0) return;
    const unsubs = channels.map(ch => {
      if (!ch.id) return () => {};
      return db
        .collection("telegram_messages")
        .where("channelDocId", "==", ch.id)
        .orderBy("date", "desc")
        .limit(10)
        .onSnapshot(snap => {
          const visible = snap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .find(m => !m.isFiltered && (m.text?.trim() || m.mediaUrl));
          if (visible) setLastMsgs(prev => ({ ...prev, [ch.id]: visible }));
        }, () => {});
    });
    return () => unsubs.forEach(u => u());
  }, [channels]);

  // Messages for selected channel
  useEffect(() => {
    if (unsubRef.current) { unsubRef.current(); unsubRef.current = null; }
    if (!selectedCh) return;

    const cacheKey = selectedCh.id;

    if (_msgsCache[cacheKey]) { setMessages(_msgsCache[cacheKey]); setLoadingMsgs(false); }
    else                       { setLoadingMsgs(true); setMessages([]); }

    const q = db
      .collection("telegram_messages")
      .where("channelDocId", "==", selectedCh.id)
      .orderBy("date", "asc")
      .limit(150);

    const chTopicId = selectedCh.topicId ?? null;

    unsubRef.current = q.onSnapshot(
      snap => {
        const msgs = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(isDisplayable)
          // If this channel is scoped to a topic, only show messages from that topic.
          .filter(msg => chTopicId === null || Number(msg.topicId) === Number(chTopicId));
        _msgsCache[cacheKey] = msgs;
        setMessages(msgs);
        setLoadingMsgs(false);
      },
      err => { console.error(err); setLoadingMsgs(false); }
    );

    return () => { if (unsubRef.current) { unsubRef.current(); unsubRef.current = null; } };
  }, [selectedCh]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  async function handleSend(e) {
    e.preventDefault();
    if (!text.trim() || sending || !selectedCh) return;
    setSending(true); setSendError("");
    try {
      await api("POST", "/send", { text: text.trim(), channelDocId: selectedCh.id });
      setText("");
    } catch (err) { setSendError(err.message); }
    finally { setSending(false); }
  }

  function handleDelete(docId) {
    setMessages(prev => prev.filter(m => m.id !== docId));
    // Invalidate cache so it reloads fresh
    if (selectedCh?.id) delete _msgsCache[selectedCh.id];
  }

  async function deleteChannel(ch) {
    if (!window.confirm(`¿Eliminar el canal "${ch.name}"?`)) return;
    try {
      await api("DELETE", `/channels/${ch.id}`);
      _channelsCache = null;
      if (selectedCh?.id === ch.id) setSelectedCh(null);
      loadChannels(true);
    } catch (e) { alert(e.message); }
  }

  // Build grouped list (date separators)
  const grouped = [];
  let lastLabel = null;
  for (const msg of messages) {
    const label = fmtDate(msg.date);
    if (label !== lastLabel) {
      grouped.push({ type: "sep", label, key: `sep-${label}` });
      lastLabel = label;
    }
    grouped.push({ type: "msg", msg, key: msg.id });
  }

  // Sidebar channel filter
  const filteredChannels = search.trim()
    ? channels.filter(ch => ch.name?.toLowerCase().includes(search.toLowerCase()))
    : channels;

  return (
    <Box sx={{ flex: 1, display: "flex", overflow: "hidden", background: TG.chatBg }}>

      {/* ── Sidebar ── */}
      <Box sx={{
        width: 292, flexShrink: 0,
        background: TG.sidebarBg,
        borderRight: `1px solid ${TG.border}`,
        display: "flex", flexDirection: "column",
        "@media (max-width: 700px)": { width: "100%", display: mobileView === "list" ? "flex" : "none" },
      }}>
        {/* Header */}
        <Box sx={{ px: 1.75, pt: 2, pb: 1.5, borderBottom: `1px solid ${TG.border}` }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.25}>
            <Typography sx={{ color: TG.text, fontWeight: 700, fontSize: 15, letterSpacing: 0.15 }}>
              Canales
            </Typography>
            <Tooltip title="Agregar canal">
              <IconButton size="small" onClick={() => { setEditCh(null); setChDialog(true); }}
                sx={{
                  color: TG.accent, width: 28, height: 28,
                  border: `1px solid rgba(82,136,193,0.35)`,
                  borderRadius: "8px",
                  "&:hover": { background: TG.accentFaint, borderColor: TG.accent },
                }}>
                <MdAdd size={15} />
              </IconButton>
            </Tooltip>
          </Stack>

          {/* Search */}
          <Box sx={{
            display: "flex", alignItems: "center", gap: 0.75,
            background: TG.inputBg, borderRadius: "10px",
            px: 1.25, py: 0.75,
            border: `1px solid ${TG.borderLight}`,
            transition: "border-color 0.15s",
            "&:focus-within": { borderColor: `${TG.accent}44` },
          }}>
            <MdSearch size={14} color={TG.textDim} style={{ flexShrink: 0 }} />
            <InputBase
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Buscar canal…"
              sx={{ fontSize: 12.5, color: TG.text, flex: 1, "& input::placeholder": { color: TG.textDim, opacity: 1 } }}
            />
            {search && (
              <IconButton size="small" onClick={() => setSearch("")} sx={{ color: TG.textDim, p: 0.25 }}>
                <MdClose size={12} />
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Channel list */}
        <Box sx={{ flex: 1, overflowY: "auto", py: 0.5,
          "&::-webkit-scrollbar": { width: "4px" },
          "&::-webkit-scrollbar-thumb": { background: "rgba(255,255,255,0.1)", borderRadius: "4px" },
        }}>
          {loadingCh ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress size={22} sx={{ color: TG.accent }} />
            </Box>
          ) : chError ? (
            <Box sx={{ px: 2.5, py: 5, textAlign: "center" }}>
              <Typography sx={{ color: "#f87171", fontSize: 12.5, mb: 1.5, lineHeight: 1.5 }}>
                {chError}
              </Typography>
              <Button size="small" onClick={() => loadChannels(true)}
                sx={{ color: TG.accent, textTransform: "none", fontSize: 12, borderColor: `${TG.accent}55`, border: "1px solid" }}>
                Reintentar
              </Button>
            </Box>
          ) : filteredChannels.length === 0 ? (
            <Box sx={{ px: 2.5, py: 5, textAlign: "center" }}>
              <Typography sx={{ color: TG.textMuted, fontSize: 13 }}>
                {channels.length === 0 ? "Sin canales configurados" : "Sin resultados"}
              </Typography>
              {channels.length === 0 && (
                <Button size="small" onClick={() => { setEditCh(null); setChDialog(true); }}
                  sx={{ mt: 1.5, color: TG.accent, textTransform: "none", fontSize: 12 }}>
                  + Agregar canal
                </Button>
              )}
            </Box>
          ) : (
            filteredChannels.map(ch => (
              <Box key={ch.id} sx={{ position: "relative", "&:hover .ch-actions": { opacity: 1 } }}>
                <ChannelItem
                  ch={ch}
                  selected={selectedCh?.id === ch.id}
                  onClick={c => { setSelectedCh(c); setMobileView("chat"); }}
                  lastMsg={lastMsgs[ch.id] || null}
                />
                <Stack direction="row" className="ch-actions"
                  sx={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", opacity: 0, transition: "opacity 0.15s", gap: 0.5 }}>
                  <IconButton size="small" onClick={() => { setEditCh(ch); setChDialog(true); }}
                    sx={{ width: 22, height: 22, background: "rgba(10,18,28,0.75)", color: TG.textMuted, backdropFilter: "blur(4px)", "&:hover": { color: TG.text } }}>
                    <MdEdit size={11} />
                  </IconButton>
                  <IconButton size="small" onClick={() => deleteChannel(ch)}
                    sx={{ width: 22, height: 22, background: "rgba(10,18,28,0.75)", color: TG.textDim, backdropFilter: "blur(4px)", "&:hover": { color: "#f87171" } }}>
                    <MdClose size={11} />
                  </IconButton>
                </Stack>
              </Box>
            ))
          )}
        </Box>

        {/* Session status bar — always visible at the bottom of the sidebar */}
        <Box sx={{ borderTop: `1px solid ${TG.border}`, pt: 0.5 }}>
          <SessionStatusBar onOpen={() => setReconnectOpen(true)} refreshKey={sessionRefresh} />
        </Box>
      </Box>

      {/* ── Chat area ── */}
      <Box sx={{
        flex: 1, display: "flex", flexDirection: "column", overflow: "hidden",
        "@media (max-width: 700px)": { display: mobileView === "chat" ? "flex" : "none" },
      }}>
        {selectedCh ? (
          <>
            {/* Chat header */}
            <Box sx={{
              px: 2.5, py: 1.375,
              background: TG.headerBg,
              borderBottom: `1px solid ${TG.border}`,
              display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0,
              boxShadow: "0 1px 0 rgba(0,0,0,0.2)",
            }}>
              <IconButton size="small" onClick={() => setMobileView("list")}
                sx={{ color: TG.textMuted, display: { xs: "flex", sm: "none" } }}>
                <MdArrowBack />
              </IconButton>
              <Avatar sx={{
                width: 36, height: 36,
                background: `linear-gradient(135deg, ${channelColor(selectedCh.id)}, ${channelColor(selectedCh.id)}99)`,
                fontSize: 14, fontWeight: 800, flexShrink: 0,
                boxShadow: "0 2px 6px rgba(0,0,0,0.35)",
              }}>
                {channelInitial(selectedCh)}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ color: TG.text, fontWeight: 700, fontSize: 14, lineHeight: 1.25 }}>
                  {selectedCh.name}
                </Typography>
                <Typography sx={{ color: TG.textDim, fontSize: 11, mt: 0.125 }}>
                  {selectedCh.channelUsername || selectedCh.channelId || "Canal de Telegram"}
                </Typography>
              </Box>
              <Tooltip title="Conexión de Telegram">
                <IconButton size="small" onClick={() => setReconnectOpen(true)}
                  sx={{
                    color: TG.textMuted, borderRadius: "8px", width: 32, height: 32, mr: 1,
                    border: `1px solid ${TG.border}`,
                    "&:hover": { color: TG.text, borderColor: `${TG.accent}55`, background: TG.accentFaint },
                  }}>
                  <MdLink size={15} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Configurar canal">
                <IconButton size="small" onClick={() => { setEditCh(selectedCh); setChDialog(true); }}
                  sx={{
                    color: TG.textMuted, borderRadius: "8px", width: 32, height: 32,
                    border: `1px solid ${TG.border}`,
                    "&:hover": { color: TG.text, borderColor: `${TG.accent}55`, background: TG.accentFaint },
                  }}>
                  <MdSettings size={15} />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Messages list */}
            <Box sx={{
              flex: 1, overflowY: "auto", overflowX: "hidden",
              px: 0, py: 2,
              background: TG.chatBg,
              backgroundImage: "radial-gradient(ellipse 60% 40% at 20% 15%, rgba(82,136,193,0.06) 0%, transparent 70%)",
              "&::-webkit-scrollbar": { width: "5px" },
              "&::-webkit-scrollbar-thumb": { background: "rgba(255,255,255,0.08)", borderRadius: "4px" },
            }}>
              {loadingMsgs ? (
                <Box sx={{ display: "flex", justifyContent: "center", pt: 10 }}>
                  <CircularProgress sx={{ color: TG.accent }} size={28} />
                </Box>
              ) : messages.length === 0 ? (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 1, opacity: 0.6 }}>
                  <Typography sx={{ color: TG.textMuted, fontSize: 14 }}>Sin mensajes en este canal</Typography>
                  <Typography sx={{ color: TG.textDim, fontSize: 11.5 }}>El poller captura mensajes nuevos cada ~15 s</Typography>
                </Box>
              ) : (
                grouped.map(item =>
                  item.type === "sep"
                    ? <DateSep key={item.key} label={item.label} />
                    : <MessageBubble key={item.key} msg={item.msg} channel={selectedCh} onDelete={handleDelete} />
                )
              )}
              <div ref={bottomRef} />
            </Box>

            <Divider sx={{ borderColor: TG.border }} />

            {/* Compose */}
            <Box component="form" onSubmit={handleSend}
              sx={{ px: 2, py: 1.5, display: "flex", gap: 1, alignItems: "flex-end", background: TG.headerBg, flexShrink: 0 }}>
              <InputBase
                multiline maxRows={6} fullWidth value={text}
                onChange={e => setText(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(e); } }}
                placeholder="Escribe un mensaje…"
                sx={{
                  color: TG.text,
                  background: TG.inputBg,
                  borderRadius: "14px",
                  px: 2, py: 1.125,
                  fontSize: 13.5, lineHeight: 1.6,
                  border: `1px solid ${TG.borderLight}`,
                  transition: "border-color 0.2s",
                  "&.Mui-focused": { borderColor: `${TG.accent}55` },
                  "& textarea::placeholder": { color: TG.textDim, opacity: 1 },
                }}
              />
              <IconButton type="submit" disabled={!text.trim() || sending}
                sx={{
                  background: text.trim() ? TG.accent : "rgba(255,255,255,0.07)",
                  color: text.trim() ? "#fff" : TG.textDim,
                  width: 40, height: 40, borderRadius: "50%", flexShrink: 0,
                  transition: "background 0.2s, color 0.2s",
                  "&:hover": { background: text.trim() ? TG.accentHover : "rgba(255,255,255,0.1)" },
                  "&.Mui-disabled": { background: "rgba(255,255,255,0.06)", color: TG.textDim },
                }}>
                {sending ? <CircularProgress size={17} sx={{ color: "#fff" }} /> : <MdSend size={17} />}
              </IconButton>
            </Box>

            {sendError && (
              <Typography variant="caption" sx={{ color: "#f87171", px: 2.5, pb: 1.25, display: "block" }}>
                {sendError}
              </Typography>
            )}
          </>
        ) : (
          /* Empty state — no channel selected */
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2, opacity: 0.7 }}>
            <Box sx={{
              width: 64, height: 64, borderRadius: "50%",
              background: `${TG.accent}1a`,
              border: `1px solid ${TG.accent}33`,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <MdSend size={26} color={TG.accent} />
            </Box>
            <Box sx={{ textAlign: "center" }}>
              <Typography sx={{ color: TG.textMuted, fontSize: 14, fontWeight: 500 }}>
                Selecciona un canal
              </Typography>
              <Typography sx={{ color: TG.textDim, fontSize: 12.5, mt: 0.5 }}>
                Los mensajes aparecen aquí
              </Typography>
            </Box>
            {channels.length === 0 && !loadingCh && (
              <Button size="small" onClick={() => { setEditCh(null); setChDialog(true); }}
                variant="outlined"
                sx={{ color: TG.accent, textTransform: "none", borderColor: `${TG.accent}55`, borderRadius: "10px", fontSize: 13 }}>
                + Agregar primer canal
              </Button>
            )}
          </Box>
        )}
      </Box>

      <ChannelDialog
        open={chDialog}
        onClose={() => setChDialog(false)}
        onSaved={() => loadChannels(true)}
        editChannel={editCh}
      />

      <ReconnectDialog
        open={reconnectOpen}
        onClose={(didReconnect) => {
          setReconnectOpen(false);
          if (didReconnect) setSessionRefresh(k => k + 1);
        }}
      />
    </Box>
  );
}
